/* The shop's ledger (see ledger.tsx), over the title screen. */

.ledger {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(255, 246, 235, 0.55);
  backdrop-filter: blur(3px);
  z-index: 10;
}

.card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(480px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  padding: 18px 22px 20px;
  border-radius: 24px;
  background: var(--panel);
  border: 1px solid var(--line);
  box-shadow: 0 12px 40px rgba(120, 80, 50, 0.15);
  animation: rise-in 0.3s ease-out;
}

@keyframes rise-in {
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

/*
 * The ledger and the kitten's résumé: side by side as two cards under their headings where
 * the screen is wide enough, each scrolled on its own; otherwise one at a time in this card,
 * chosen by the tabs.
 */
.pages {
  display: contents;
}

.page {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.heading {
  display: none;
  margin: 0;
  font-size: 20px;
  text-align: center;
}

.card[data-tab="ledger"] [data-page="resume"],
.card[data-tab="resume"] [data-page="ledger"] {
  display: none;
}

/* The tabs, as the card's heading where the two are shown one at a time. */
.tabs {
  display: flex;
  justify-content: center;
  gap: 6px;
}

.tab {
  padding: 4px 14px;
  border: none;
  border-radius: 99px;
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
}

.tab[aria-selected="true"] {
  background: var(--accent-soft);
  color: var(--ink);
}

.summary {
  margin: -4px 0 0;
  font-size: 13px;
  text-align: center;
  color: var(--ink-soft);
}

.summary b {
  font-size: 17px;
  color: var(--ink);
}

.levelName {
  margin: 4px 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--ink-soft);
}

.entries {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* A line of the ledger: the rank stamp, the request, its numbers and the wish. */
.entry {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 4px 12px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fffaf3;
}

.rank {
  grid-row: 1 / 4;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--accent);
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}

.rank[data-rank="S"],
.rank[data-rank="SS"] {
  background: linear-gradient(135deg, #f7c948, #e8962e);
  box-shadow: 0 0 0 3px rgba(247, 201, 72, 0.35);
}

.rank[data-rank="SS"] {
  font-size: 15px;
}

.rank[data-rank="B"],
.rank[data-rank="C"],
.rank[data-rank="D"] {
  background: var(--mint);
}

.notYet .rank {
  background: rgba(122, 86, 68, 0.12);
  color: var(--ink-soft);
}

.what {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 8px;
}

.title {
  font-size: 15px;
  font-weight: 700;
}

.client {
  font-size: 12px;
  color: var(--ink-soft);
}

/* The customer's face by their name. */
.face {
  width: 24px;
  height: 24px;
  margin: -6px 4px -6px 0;
  vertical-align: middle;
}

.numbers {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 14px;
  font-size: 12px;
  color: var(--ink-soft);
}

.numbers b {
  font-size: 14px;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.none {
  font-size: 12px;
  color: var(--ink-soft);
}

.wish {
  justify-self: start;
  padding: 2px 8px;
  border-radius: 99px;
  background: rgba(122, 86, 68, 0.08);
  font-size: 11px;
  font-weight: 700;
  color: var(--ink-soft);
}

/* The wish has come true: its tag is stamped. */
.met {
  background: var(--accent-soft);
  color: var(--accent);
}

.met::before {
  content: "✓ ";
}

.actions button {
  flex: 1;
}

/* The kitten's résumé (see ResumePage). */
.resume {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Name, place and title as a résumé's ruled fields, the ID photo at their right. */
.profile {
  display: flex;
  align-items: center;
  gap: 14px;
}

.fields {
  flex: 1;
  margin: 0;
  border-top: 1px solid var(--line);
}

.fields div {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 6px 4px;
  border-bottom: 1px solid var(--line);
}

.fields dt {
  width: 3.5em;
  font-size: 11px;
  color: var(--ink-soft);
}

.fields dd {
  margin: 0;
  font-size: 14px;
}

.fields .name {
  font-size: 18px;
  letter-spacing: 0.3em;
}

.rankTitle {
  font-weight: 700;
  color: var(--accent);
}

.photo {
  width: 96px;
  height: 116px;
  object-fit: cover;
  border: 5px solid #fff;
  border-radius: 3px;
  background: #dcebf4;
  box-shadow: 0 0 0 1px var(--line), 0 2px 6px rgba(120, 80, 50, 0.12);
  transform: rotate(1.5deg);
}

.rates,
.badges {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.rate {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px;
  font-size: 13px;
}

.rate b {
  font-variant-numeric: tabular-nums;
}

.bar {
  grid-column: 1 / -1;
  height: 8px;
  border-radius: 99px;
  background: rgba(122, 86, 68, 0.1);
  overflow: hidden;
}

.bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--mint);
}

.badges {
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
}

/* A badge: the medal, its name, and how it is earned. Not yet earned, it is faded. */
.badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 8px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fffaf3;
  text-align: center;
  color: var(--ink-soft);
}

.badge b {
  font-size: 13px;
}

.badge small {
  font-size: 11px;
}

.medal {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(122, 86, 68, 0.12);
  color: #fff;
}

.medal svg {
  width: 24px;
  height: 24px;
}

.earned {
  color: var(--ink);
}

.earned .medal {
  background: linear-gradient(135deg, #f7c948, #e8962e);
  box-shadow: 0 0 0 3px rgba(247, 201, 72, 0.35);
}

/*
 * Wide enough for the ledger and the résumé side by side (see .pages): each is a card of its
 * own, and the frame round them is only their place, with 閉じる under both.
 */
@media (min-width: 960px) {
  .card {
    width: min(960px, calc(100vw - 32px));
    overflow: hidden;
    padding: 0;
    border: none;
    border-radius: 0;
    background: none;
    box-shadow: none;
  }

  .tabs {
    display: none;
  }

  .heading {
    display: block;
  }

  .pages {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
    gap: 24px;
    min-height: 0;
  }

  .card[data-tab] .page {
    display: flex;
    min-height: 0;
    overflow: hidden auto;
    padding: 18px 22px 20px;
    border-radius: 24px;
    background: var(--panel);
    border: 1px solid var(--line);
    box-shadow: 0 12px 40px rgba(120, 80, 50, 0.15);
  }

  /* The résumé is short: its card ends where it does. */
  .card[data-tab] [data-page="resume"] {
    align-self: start;
    max-height: 100%;
  }

  .actions {
    justify-content: center;
  }

  .actions button {
    flex: 0 1 240px;
  }
}
